
import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import { SRTBlock, ToneLevel, AppStatus, MovieContext, SUPPORTED_MODELS, ApiConnectionStatus } from './types';
import { parseSRT, buildSRT, downloadFile, timeToMs, msToTime } from './utils/srtParser';
import { translateBatch, verifyConnection, setApiKey, analyzeSubtitleContext, detectMovieInfoFromFilename, analyzeCharacterRelationships, translateSingleBlock, normalizeCustomStylePrompt } from './services/geminiService';
import SettingsPanel from './components/SettingsPanel';

// Context window vẫn giữ cố định để đảm bảo nối tiếp hội thoại
const CONTEXT_WINDOW_SIZE = 10; 
const SIGNATURE_TEXT = "<i>Phụ đề được hiệu chỉnh và dịch bởi tool AI Sub GG. Ủng hộ nhà phát triển bằng cách chuyển khoản tới <b>STK: 888 999 13 095 </b>(ngân hàng Techcombank).</i>";

// --- MINIMAP COMPONENT ---
const Minimap: React.FC<{
  blocks: SRTBlock[];
  scrollRef: React.RefObject<HTMLDivElement>;
  isDarkMode: boolean;
}> = ({ blocks, scrollRef, isDarkMode }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Colors based on block status
  const getColor = (block: SRTBlock) => {
    if (block.feedbackStatus === 'dislike') return isDarkMode ? '#ef4444' : '#dc2626'; // Red
    if (block.feedbackStatus === 'like') return isDarkMode ? '#a855f7' : '#9333ea'; // Purple
    if (block.isManuallyEdited) return isDarkMode ? '#fbbf24' : '#d97706'; // Amber
    if (block.isReadOnly) return isDarkMode ? '#3f3f46' : '#9ca3af'; // Dark Zinc
    if (block.text !== block.originalText) return isDarkMode ? '#34d399' : '#059669'; // Emerald
    return isDarkMode ? '#71717a' : '#9ca3af'; // Zinc-500 / Gray-400
  };

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const scrollContainer = scrollRef.current;
    if (!canvas || !scrollContainer || blocks.length === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const totalBlocks = blocks.length;
    
    ctx.clearRect(0, 0, width, height);

    const blockHeight = height / totalBlocks; 
    const renderWords = blockHeight >= 3;

    blocks.forEach((block, index) => {
      const y = index * blockHeight;
      ctx.fillStyle = getColor(block);

      if (renderWords) {
        const words = block.originalText.split(/\s+/).slice(0, 8);
        let currentX = 4; 
        const rowH = Math.min(blockHeight - 1, 2); 
        const yOffset = y + (blockHeight - rowH) / 2;

        for (let i = 0; i < words.length; i++) {
            const wordLen = Math.min(words[i].length * 2, 20); 
            if (currentX + wordLen > width - 4) break; 
            ctx.fillRect(currentX, yOffset, wordLen, rowH);
            currentX += wordLen + 2; 
        }
      } else {
        const visualLength = Math.min((block.originalText.length * 0.5) + 10, width - 6);
        const lineH = Math.max(1, blockHeight); 
        ctx.fillRect(2, y, visualLength, lineH);
      }
    });

    // Draw Viewport Overlay
    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    if (scrollHeight > clientHeight) {
      const viewportY = (scrollTop / scrollHeight) * height;
      const viewportH = (clientHeight / scrollHeight) * height;
      
      ctx.fillStyle = isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.05)';
      ctx.fillRect(0, viewportY, width, viewportH); 
      
      ctx.fillStyle = isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(37, 99, 235, 0.5)'; 
      ctx.fillRect(0, viewportY, 2, viewportH); 
    }

  }, [blocks, isDarkMode, scrollRef]);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => requestAnimationFrame(draw);
    scrollContainer.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', draw);
    draw();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', draw);
    };
  }, [draw, scrollRef]);

  const handleMinimapInteraction = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const scrollContainer = scrollRef.current;
    if (!canvas || !scrollContainer) return;

    const rect = canvas.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const percentage = Math.max(0, Math.min(1, y / rect.height));
    const targetScrollTop = (percentage * scrollContainer.scrollHeight) - (scrollContainer.clientHeight / 2);
    
    scrollContainer.scrollTo({ top: targetScrollTop, behavior: 'auto' });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDragging(true);
    handleMinimapInteraction(e);
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging) handleMinimapInteraction(e);
  };
  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && canvas.parentElement) {
      canvas.width = canvas.parentElement.clientWidth;
      canvas.height = canvas.parentElement.clientHeight;
      draw();
    }
  }, [draw]);

  return (
    <div className={`w-24 h-full flex-shrink-0 border-l ${isDarkMode ? 'border-white/10 bg-[#0a0a0a]' : 'border-gray-200 bg-gray-50'} relative select-none`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-pointer"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </div>
  );
};

// --- SUBTITLE ROW COMPONENT ---
interface SubtitleRowProps {
  block: SRTBlock;
  isProcessing: boolean;
  isTranslated: boolean;
  isInRange: boolean;
  isDarkMode: boolean;
  zoomLevel: number;
  onEdit: (id: number, text: string) => void;
  onFeedback: (id: number, type: 'like' | 'dislike') => void;
  onAiRetranslate: (id: number) => void;
}

const SubtitleRow: React.FC<SubtitleRowProps> = React.memo(({
  block, isProcessing, isTranslated, isInRange, isDarkMode, zoomLevel, onEdit, onFeedback, onAiRetranslate
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const adjustHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'; 
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${scrollHeight}px`;
    }
  }, []);

  useLayoutEffect(() => {
    adjustHeight();
  }, [block.text, zoomLevel, isProcessing, adjustHeight]);

  useEffect(() => {
      if (!containerRef.current) return;
      // Use requestAnimationFrame to avoid "ResizeObserver loop completed with undelivered notifications"
      const observer = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          adjustHeight();
        });
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
  }, [adjustHeight]);

  const getTextBlockColor = () => {
    if (block.isReadOnly) return isDarkMode ? 'text-zinc-500 italic' : 'text-gray-500 italic';
    if (block.feedbackStatus === 'like') return 'text-purple-500';
    if (block.feedbackStatus === 'dislike') return 'text-red-500'; 
    if (block.isManuallyEdited) return isDarkMode ? 'text-amber-400' : 'text-amber-600';
    if (isTranslated) return isDarkMode ? 'text-emerald-400' : 'text-emerald-600';
    return isDarkMode ? 'text-zinc-400' : 'text-gray-600';
  };

  const gradientColor = block.isRetranslating ? '#06b6d4' : '#3b82f6';
  const secondaryGradientColor = block.isRetranslating ? '#22d3ee' : '#60a5fa';
  const shadowClass = block.isRetranslating ? 'shadow-cyan-500/20' : 'shadow-blue-500/20';

  const containerClasses = isProcessing 
    ? `relative overflow-hidden p-[4px] rounded-xl shadow-2xl ${shadowClass}` 
    : `group flex gap-6 p-4 rounded-xl border transition-all duration-300 items-start 
       ${block.isReadOnly 
            ? (isDarkMode ? 'bg-zinc-900/30 border-zinc-800' : 'bg-gray-100 border-gray-200') 
            : (isDarkMode ? 'bg-[#18181b] hover:bg-zinc-900 border-zinc-800 hover:border-zinc-700' : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300 shadow-sm')}`;

  const opacityClass = !isInRange && !block.isReadOnly ? 'opacity-40 grayscale blur-[1px]' : 'opacity-100';
  const innerBgClass = isDarkMode ? 'bg-[#18181b]' : 'bg-white';

  return (
    <div id={`block-${block.id}`} className={`${containerClasses} ${opacityClass}`}>
       {isProcessing && (
           <>
                <div 
                    className="absolute inset-[-200%] animate-[spin_3s_linear_infinite] opacity-100" 
                    style={{ background: `conic-gradient(transparent 80deg, ${gradientColor} 360deg)` }}
                />
                <div 
                    className="absolute inset-[-200%] animate-[spin_3s_linear_infinite] blur-lg opacity-80" 
                    style={{ background: `conic-gradient(transparent 80deg, ${secondaryGradientColor} 360deg)` }}
                />
           </>
       )}

       <div className={`relative w-full rounded-[9px] flex gap-6 items-start ${isProcessing ? `p-4 ${innerBgClass}` : ''}`}>
           <div 
              className={`flex-shrink-0 flex flex-col justify-start border-r pr-3 pt-1 ${isDarkMode ? 'border-zinc-800' : 'border-gray-200'}`}
              style={{ width: `${Math.max(70, zoomLevel * 5.5)}px` }}
           >
              <span 
                className={`font-bold mb-1 ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}
                style={{ fontSize: `${Math.max(10, Math.round(zoomLevel * 0.75))}px` }}
              >
                #{block.id}
              </span>
              <span 
                className={`font-mono ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}
                style={{ fontSize: `${Math.max(10, Math.round(zoomLevel * 0.75))}px` }}
              >
                {block.startTime.split(',')[0]}
              </span>
              {block.isReadOnly && (
                   <div className="mt-2 text-zinc-500" title="Dòng chữ ký">
                       <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
                   </div>
              )}
           </div>

           <div 
                className={`flex-1 leading-relaxed select-text min-w-0 pt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}
                style={{ fontSize: `${zoomLevel}px` }}
                dangerouslySetInnerHTML={{ __html: block.originalText }} 
            />

           <div className="flex-1 relative min-w-0" ref={containerRef}>
             {!block.isReadOnly && (
                 <div className={`absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 rounded-full px-1.5 py-0.5 border shadow-lg ${isDarkMode ? 'bg-black/90 border-zinc-700' : 'bg-white/90 border-gray-200'}`}>
                    <button onClick={() => onAiRetranslate(block.id)} className={`p-1 hover:text-cyan-400 transition-colors ${block.isRetranslating ? 'text-cyan-400 animate-pulse' : 'text-zinc-500'}`} title="Dịch lại bằng AI"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"></path></svg></button>
                    <div className={`w-px mx-1 h-3 self-center ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-300'}`}></div>
                    <button onClick={() => onFeedback(block.id, 'like')} className={`p-1 hover:text-purple-500 transition-colors ${block.feedbackStatus === 'like' ? 'text-purple-500' : 'text-zinc-500'}`} title="Thích"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M14 9V5a3 3 0 00-3-3l-4 9v11h11.28a2 2 0 002-1.7l1.38-9a2 2 0 00-2-2.3zM7 22H4a2 2 0 01-2-2v-7a2 2 0 012-2h3"></path></svg></button>
                    <div className={`w-px mx-1 h-3 self-center ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-300'}`}></div>
                    <button onClick={() => onFeedback(block.id, 'dislike')} className={`p-1 hover:text-red-500 transition-colors ${block.feedbackStatus === 'dislike' ? 'text-red-500' : 'text-zinc-500'}`} title="Cần xem lại"><svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M10 15v4a3 3 0 003 3l4-9V2H5.72a2 2 0 00-2 1.7l-1.38 9a2 2 0 002 2.3zm7-13h2.67A2.31 2.31 0 0122 4v7a2.31 2.31 0 01-2.33 2H17"></path></svg></button>
                 </div>
             )}

             {block.isReadOnly ? (
                 <div style={{ fontSize: `${zoomLevel}px` }} className={`w-full p-0 font-medium leading-relaxed pt-1 ${getTextBlockColor()}`} dangerouslySetInnerHTML={{ __html: block.text }} />
             ) : (
                 (status === AppStatus.Translating && isProcessing && !isTranslated) ? (
                    <div className="flex items-center gap-2 h-full pt-1">
                        <span className="flex h-2 w-2 relative">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
                        </span>
                        <span className="text-sm font-medium text-blue-400 animate-pulse">AI đang suy nghĩ...</span>
                    </div>
                 ) : (
                   <textarea 
                      ref={textareaRef}
                      value={block.text}
                      onChange={(e) => onEdit(block.id, e.target.value)}
                      spellCheck={false}
                      style={{ fontSize: `${zoomLevel}px` }}
                      className={`w-full bg-transparent border-none focus:ring-0 focus:border-b focus:border-blue-500/50 rounded-none p-0 font-medium resize-none overflow-hidden leading-relaxed transition-colors pt-1 ${getTextBlockColor()}`}
                   />
                 )
             )}
           </div>
       </div>
    </div>
  );
});

const App: React.FC = () => {
  // Theme State
  const [themeMode, setThemeMode] = useState<'light' | 'dark' | 'auto'>('auto');
  const [systemIsDark, setSystemIsDark] = useState(() => window.matchMedia('(prefers-color-scheme: dark)').matches);
  const [isThemeDropdownOpen, setIsThemeDropdownOpen] = useState(false);
  const [isFooterExpanded, setIsFooterExpanded] = useState(true);
  const [showDonatePopup, setShowDonatePopup] = useState(false);
  
  // New States for Requirements
  const [showAnalyzeConfirm, setShowAnalyzeConfirm] = useState(false);
  const [showRelEditor, setShowRelEditor] = useState(false);
  const [showStyleEditor, setShowStyleEditor] = useState(false);
  const [subtitleColor, setSubtitleColor] = useState("#00FFFF"); // Default Cyan (HEX)
  const [isColorEnabled, setIsColorEnabled] = useState(false);
  
  // Dialect State (Default Southern Style ON)
  const [isSouthernStyle, setIsSouthernStyle] = useState<boolean>(true);
  
  // Custom Style State
  const [customStyle, setCustomStyle] = useState<string>("");
  const [isNormalizingStyle, setIsNormalizingStyle] = useState<boolean>(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setSystemIsDark(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  const isDarkMode = themeMode === 'auto' ? systemIsDark : themeMode === 'dark';
  
  // Theme Classes (Adjusted for Softer Contrast)
  const bgMain = isDarkMode ? 'bg-zinc-950' : 'bg-zinc-50'; // Softer Black / Off White
  const bgSidebar = isDarkMode ? 'bg-zinc-950' : 'bg-white';
  const textMain = isDarkMode ? 'text-zinc-200' : 'text-zinc-900';
  const borderMain = isDarkMode ? 'border-white/10' : 'border-zinc-200';
  const textSub = isDarkMode ? 'text-zinc-500' : 'text-zinc-500';
  const toolbarBg = isDarkMode ? 'bg-zinc-950/90' : 'bg-white/90';

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
  }, [isDarkMode]);

  const [blocks, setBlocks] = useState<SRTBlock[]>([]);
  const [filename, setFilename] = useState<string>("subtitle.srt");
  const [status, setStatus] = useState<AppStatus>(AppStatus.Idle);
  const [progress, setProgress] = useState<number>(0);
  const [currentBlockIndex, setCurrentBlockIndex] = useState<number>(0);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  
  const [model, setModel] = useState<string>(SUPPORTED_MODELS[0].id);
  const [tone, setTone] = useState<ToneLevel>(ToneLevel.Casual);
  const [context, setContext] = useState<MovieContext>({ title: '', plot: '' });
  const [batchSize, setBatchSize] = useState<number>(10);
  const [rangeStart, setRangeStart] = useState<number>(1);
  const [rangeEnd, setRangeEnd] = useState<number>(0);
  
  const [charRelationships, setCharRelationships] = useState<string>("");
  const [isCensorshipEnabled, setIsCensorshipEnabled] = useState<boolean>(false);
  const [isMetricConversionEnabled, setIsMetricConversionEnabled] = useState<boolean>(false);
  const [isHiTranslationEnabled, setIsHiTranslationEnabled] = useState<boolean>(false);
  const [isLyricsTranslationEnabled, setIsLyricsTranslationEnabled] = useState<boolean>(false);
  const [isBilingual, setIsBilingual] = useState<boolean>(false);
  
  // Default Clean Mode to ON
  const [isCleanModeEnabled, setIsCleanModeEnabled] = useState<boolean>(true);

  const settingsRef = useRef({
      isBilingual,
      isCensorshipEnabled,
      isMetricConversionEnabled,
      isCleanModeEnabled,
      isHiTranslationEnabled,
      isLyricsTranslationEnabled,
      charRelationships,
      subtitleColor,
      isColorEnabled,
      isSouthernStyle,
      customStyle
  });

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
      settingsRef.current = {
          isBilingual,
          isCensorshipEnabled,
          isMetricConversionEnabled,
          isCleanModeEnabled,
          isHiTranslationEnabled,
          isLyricsTranslationEnabled,
          charRelationships,
          subtitleColor,
          isColorEnabled,
          isSouthernStyle,
          customStyle
      };
  }, [isBilingual, isCensorshipEnabled, isMetricConversionEnabled, isCleanModeEnabled, isHiTranslationEnabled, isLyricsTranslationEnabled, charRelationships, subtitleColor, isColorEnabled, isSouthernStyle, customStyle]);

  useEffect(() => {
    if (status === AppStatus.Translating) {
        timerRef.current = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
    } else {
        if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [status]);

  useEffect(() => {
    if (blocks.length > 0 && rangeStart > 0) {
        const element = document.getElementById(`block-${rangeStart}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }
  }, [rangeStart, blocks.length]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const [zoomLevel, setZoomLevel] = useState<number>(14);
  const [sidebarWidth, setSidebarWidth] = useState<number>(400);
  const [isResizing, setIsResizing] = useState<boolean>(false);

  const [confirmation, setConfirmation] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    confirmColor: 'red' | 'blue';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: '',
    confirmColor: 'blue',
    onConfirm: () => {},
  });

  const [apiStatus, setApiStatus] = useState<ApiConnectionStatus>('unknown');
  const [userApiKey, setUserApiKey] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const [isFilenameAnalyzing, setIsFilenameAnalyzing] = useState<boolean>(false);
  const [isRelationshipAnalyzing, setIsRelationshipAnalyzing] = useState<boolean>(false);

  const abortControllerRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const savedKey = localStorage.getItem('gemini_api_key');
    const envKey = process.env.API_KEY;
    if (savedKey) {
        setUserApiKey(savedKey);
        setApiKey(savedKey);
    } else if (envKey) {
        setUserApiKey(envKey);
        setApiKey(envKey);
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current && status === AppStatus.Translating) {
        const element = document.getElementById(`block-${currentBlockIndex}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }
  }, [currentBlockIndex, status]);

  const startResizing = useCallback(() => { setIsResizing(true); }, []);
  const stopResizing = useCallback(() => { setIsResizing(false); }, []);
  const resize = useCallback((mouseEvent: MouseEvent) => {
    if (isResizing) {
      setSidebarWidth(mouseEvent.clientX);
    }
  }, [isResizing]);

  useEffect(() => {
    window.addEventListener('mousemove', resize);
    window.addEventListener('mouseup', stopResizing);
    return () => {
      window.removeEventListener('mousemove', resize);
      window.removeEventListener('mouseup', stopResizing);
    };
  }, [resize, stopResizing]);

  const processFile = (file: File) => {
    setFilename(file.name);
    setStatus(AppStatus.Parsing);
    const reader = new FileReader();
    
    reader.onload = (event) => {
      const content = event.target?.result as string;

      if (file.name.toLowerCase().endsWith('.json')) {
        // HANDLE JSON PROJECT FILE
        try {
          const projectData = JSON.parse(content);
          // Restore Blocks
          setBlocks(projectData.blocks || []);
          // Restore Settings
          if (projectData.settings) {
             const s = projectData.settings;
             if (s.model) setModel(s.model);
             if (s.tone) setTone(s.tone);
             if (s.batchSize) setBatchSize(s.batchSize);
             if (s.rangeStart) setRangeStart(s.rangeStart);
             if (s.rangeEnd) setRangeEnd(s.rangeEnd);
             if (s.charRelationships) setCharRelationships(s.charRelationships);
             if (s.isCensorshipEnabled !== undefined) setIsCensorshipEnabled(s.isCensorshipEnabled);
             if (s.isMetricConversionEnabled !== undefined) setIsMetricConversionEnabled(s.isMetricConversionEnabled);
             if (s.isCleanModeEnabled !== undefined) setIsCleanModeEnabled(s.isCleanModeEnabled);
             if (s.isHiTranslationEnabled !== undefined) setIsHiTranslationEnabled(s.isHiTranslationEnabled);
             if (s.isLyricsTranslationEnabled !== undefined) setIsLyricsTranslationEnabled(s.isLyricsTranslationEnabled);
             if (s.isBilingual !== undefined) setIsBilingual(s.isBilingual);
             if (s.subtitleColor) setSubtitleColor(s.subtitleColor);
             if (s.isColorEnabled !== undefined) setIsColorEnabled(s.isColorEnabled);
             if (s.isSouthernStyle !== undefined) setIsSouthernStyle(s.isSouthernStyle);
             if (s.customStyle !== undefined) setCustomStyle(s.customStyle);
          }
          // Restore Context
          if (projectData.context) {
             setContext(projectData.context);
          }
          if (projectData.filename) {
             setFilename(projectData.filename);
          }

          setStatus(AppStatus.Idle);
          setProgress(0);
          setCurrentBlockIndex(0);
          setElapsedTime(0);
          alert("Đã tải dự án thành công!");

        } catch (e) {
          console.error("Failed to parse JSON", e);
          alert("File dự án bị lỗi hoặc không đúng định dạng.");
          setStatus(AppStatus.Idle);
        }
        return;
      }

      // HANDLE SRT FILE
      const parsed = parseSRT(content);
      const shiftedBlocks = parsed.map(b => ({ ...b, id: b.id + 1 }));
      const startSignature: SRTBlock = {
          id: 1,
          startTime: "00:00:00,000",
          endTime: "00:00:30,000",
          text: SIGNATURE_TEXT,
          originalText: SIGNATURE_TEXT,
          isReadOnly: true
      };
      let endSignature: SRTBlock | null = null;
      if (shiftedBlocks.length > 0) {
          const lastBlock = shiftedBlocks[shiftedBlocks.length - 1];
          const lastEndTimeMs = timeToMs(lastBlock.endTime);
          const newStartMs = lastEndTimeMs + 5000;
          const newEndMs = newStartMs + 60000;
          endSignature = {
              id: lastBlock.id + 1,
              startTime: msToTime(newStartMs),
              endTime: msToTime(newEndMs),
              text: SIGNATURE_TEXT,
              originalText: SIGNATURE_TEXT,
              isReadOnly: true
          };
      }
      const finalBlocks = [startSignature, ...shiftedBlocks];
      if (endSignature) finalBlocks.push(endSignature);

      setBlocks(finalBlocks);
      setStatus(AppStatus.Idle);
      setProgress(0);
      setCurrentBlockIndex(0);
      setElapsedTime(0);
      
      if (finalBlocks.length > 0) {
        setRangeStart(2);
        setRangeEnd(Math.max(2, finalBlocks.length - 1));
      }
    };
    reader.readAsText(file);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    processFile(file);
  };

  const handleFileDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (status === AppStatus.Translating) return;
    const file = e.dataTransfer.files?.[0];
    if (file && (file.name.toLowerCase().endsWith('.srt') || file.name.toLowerCase().endsWith('.json'))) {
      processFile(file);
    } else {
      alert("Vui lòng tải lên file .SRT hoặc file dự án .JSON hợp lệ.");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleBlockEdit = useCallback((id: number, newText: string) => {
    setBlocks(prev => {
      const newBlocks = [...prev];
      const index = newBlocks.findIndex(b => b.id === id);
      if (index !== -1 && !newBlocks[index].isReadOnly) {
        newBlocks[index] = { 
            ...newBlocks[index], 
            text: newText,
            isManuallyEdited: true 
        };
      }
      return newBlocks;
    });
  }, []);

  const handleBlockFeedback = useCallback((id: number, type: 'like' | 'dislike') => {
    setBlocks(prev => {
      const newBlocks = [...prev];
      const index = newBlocks.findIndex(b => b.id === id);
      if (index !== -1) {
        const currentStatus = newBlocks[index].feedbackStatus;
        const newStatus = currentStatus === type ? 'none' : type;
        newBlocks[index] = { 
            ...newBlocks[index], 
            feedbackStatus: newStatus
        };
      }
      return newBlocks;
    });
  }, []);

  // Post-processing helper to clean up text if Clean Mode is on
  const postProcessText = (text: string, isCleanMode: boolean): string => {
      let processed = text;
      
      if (isCleanMode) {
          // Remove HI annotations like (LAUGHING), [MUSIC], ♪
          processed = processed.replace(/\[.*?\]/g, '')
                               .replace(/\(.*?\)/g, '')
                               .replace(/[♪♫]/g, '');
      }

      // Remove Speaker Labels (e.g. "MAN:", "TOP HAT:") if they remain
      // This regex looks for uppercase words followed by a colon at start of line
      processed = processed.replace(/(?:^|\n)-?\s*[A-Z0-9\s]+:\s*/g, '');

      // Cleanup hyphens
      // 1. Remove hyphens on empty lines
      processed = processed.replace(/^\s*-\s*$/gm, '');
      // 2. Fix multiple hyphens
      processed = processed.replace(/-\s*-\s*/g, '- ');
      // 3. If single line starts with hyphen, remove it (single speaker shouldn't have hyphen)
      if (!processed.includes('\n')) {
          processed = processed.replace(/^\s*-\s*/, '');
      }
      // 4. Remove empty lines created by removals
      processed = processed.replace(/\n\s*\n/g, '\n').trim();

      return processed;
  };

  const handleAiRetranslateAction = async (id: number) => {
       const blockIndex = blocks.findIndex(b => b.id === id);
       if (blockIndex === -1) return;
       setBlocks(prev => {
          const newBlocks = [...prev];
          newBlocks[blockIndex] = { ...newBlocks[blockIndex], isRetranslating: true };
          return newBlocks;
       });
       try {
          const targetBlock = blocks[blockIndex];
          const startCtx = Math.max(0, blockIndex - 5);
          const endCtx = Math.min(blocks.length, blockIndex + 6);
          const contextBlocks = blocks.slice(startCtx, endCtx);
          const currentSettings = settingsRef.current;

          let newText = await translateSingleBlock(
              targetBlock,
              contextBlocks,
              model,
              tone,
              context,
              currentSettings.charRelationships,
              currentSettings.isCensorshipEnabled,
              currentSettings.isMetricConversionEnabled,
              currentSettings.isCleanModeEnabled,
              currentSettings.isHiTranslationEnabled,
              currentSettings.isLyricsTranslationEnabled,
              currentSettings.isSouthernStyle,
              currentSettings.customStyle
          );

           setBlocks(prev => {
              const newBlocks = [...prev];
              const originalBlock = newBlocks[blockIndex];
              
              // Apply Post-Processing Cleaning
              let finalText = postProcessText(newText, currentSettings.isCleanModeEnabled);

              if (currentSettings.isBilingual) {
                  let sourceText = originalBlock.originalText;
                  if (currentSettings.isCleanModeEnabled) {
                      sourceText = postProcessText(sourceText, true);
                  }
                  if (sourceText) {
                      finalText = `${sourceText}\n${finalText}`;
                  }
              }

              newBlocks[blockIndex] = {
                  ...newBlocks[blockIndex],
                  text: finalText,
                  isRetranslating: false,
                  isManuallyEdited: false,
                  feedbackStatus: 'none'
              };
              return newBlocks;
          });
      } catch (error) {
          console.error("Re-translation failed", error);
          setBlocks(prev => {
              const newBlocks = [...prev];
              newBlocks[blockIndex] = { ...newBlocks[blockIndex], isRetranslating: false };
              return newBlocks;
          });
          alert("Không thể dịch lại dòng này. Vui lòng thử lại.");
      }
  };
  
  const handleVerifyApi = async () => {
    if (!userApiKey.trim()) {
        alert("Vui lòng nhập API Key trước.");
        return;
    }
    setApiStatus('verifying');
    setApiKey(userApiKey);
    const isValid = await verifyConnection();
    if (isValid) {
      setApiStatus('valid');
      localStorage.setItem('gemini_api_key', userApiKey);
    } else {
      setApiStatus('invalid');
      alert("Xác thực thất bại: Key không hợp lệ hoặc lỗi mạng.");
    }
  };

  const handleFilenameAnalyze = async () => {
      if (!userApiKey || !filename) return;
      setIsFilenameAnalyzing(true);
      setApiKey(userApiKey);
      try {
          const result = await detectMovieInfoFromFilename(filename, model);
          setContext(result);
      } catch (error) {
          console.error("Filename analyze failed", error);
          alert("Không thể nhận diện phim từ tên file. Vui lòng thử nhập tay.");
      } finally {
          setIsFilenameAnalyzing(false);
      }
  };

  const handleAutoAnalyze = async () => {
    // Show confirm dialog first
    setShowAnalyzeConfirm(true);
  };

  const executeAutoAnalyze = async () => {
    setShowAnalyzeConfirm(false);
    if (!userApiKey || blocks.length === 0) return;
    setIsAnalyzing(true);
    setApiKey(userApiKey);
    try {
        const firstChunk = blocks.slice(0, 100);
        const middleIndex = Math.floor(blocks.length / 2);
        const middleChunk = blocks.slice(middleIndex, middleIndex + 50);
        const lastChunk = blocks.slice(-50);
        const sampleBlocks = [...firstChunk, ...middleChunk, ...lastChunk];

        const result = await analyzeSubtitleContext(sampleBlocks, model);
        setContext(result);
    } catch (error) {
        console.error("Auto analyze failed", error);
        alert("Không thể phân tích ngữ cảnh. Vui lòng thử lại.");
    } finally {
        setIsAnalyzing(false);
    }
  };

  const handleRelationshipAnalyze = async () => {
      if (!userApiKey || !context.title) return;
      setIsRelationshipAnalyzing(true);
      setApiKey(userApiKey);
      try {
          const relationships = await analyzeCharacterRelationships(context.title, context.plot, model);
          setCharRelationships(relationships);
      } catch (error) {
          console.error("Relationship analyze failed", error);
          alert("Không thể phân tích quan hệ nhân vật. Vui lòng thử lại hoặc nhập tay.");
      } finally {
          setIsRelationshipAnalyzing(false);
      }
  };

  const handleNormalizeStyle = async () => {
      if (!userApiKey || !customStyle) return;
      setIsNormalizingStyle(true);
      setApiKey(userApiKey);
      try {
          const result = await normalizeCustomStylePrompt(customStyle, model);
          setCustomStyle(result);
      } catch (error) {
          console.error("Normalize style failed", error);
          alert("Không thể chuẩn hoá prompt văn phong. Vui lòng thử lại.");
      } finally {
          setIsNormalizingStyle(false);
      }
  };

  const handleSelectAllRange = () => {
      if (blocks.length > 0) {
          setRangeStart(2);
          setRangeEnd(Math.max(2, blocks.length - 1));
      }
  };

  // CLEAN UP (BROOM) FUNCTION
  const handleCleanUp = () => {
      if (blocks.length === 0) return;
      
      setConfirmation({
        isOpen: true,
        title: "Dọn dẹp dòng trắng",
        message: "Hành động này sẽ xóa bỏ các dòng phụ đề TRỐNG hoặc VÔ NGHĨA (chỉ có dấu gạch ngang, chấm, hoặc không có chữ) và sắp xếp lại toàn bộ thứ tự ID. Bạn có chắc chắn không?",
        confirmLabel: "Xác nhận dọn dẹp",
        confirmColor: "red",
        onConfirm: () => {
             const cleanedBlocks = blocks.filter(b => {
                // Keep read-only blocks (signatures)
                if (b.isReadOnly) return true;
                
                // Clean text content for check
                const text = b.text.trim();
                
                // Conditions to remove block:
                // 1. Empty string
                if (text.length === 0) return false;
                // 2. Only hyphens or whitespace
                if (/^[-–—\s]+$/.test(text)) return false;
                // 3. Only punctuation
                if (/^[.,!?;:]+$/.test(text)) return false;

                return true;
            }).map((b, index) => ({
                ...b,
                id: index + 1 // Re-index
            }));

            setBlocks(cleanedBlocks);
            // Update range end if it exceeds new length
            if (rangeEnd > cleanedBlocks.length) {
                setRangeEnd(cleanedBlocks.length);
            }
            setConfirmation(prev => ({ ...prev, isOpen: false }));
        }
      });
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 2, 32));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 2, 10));

  // Helper to inject color tags into subtitle text for download
  const injectColorTags = (blocks: SRTBlock[]): SRTBlock[] => {
      const settings = settingsRef.current;
      if (!settings.isColorEnabled || !settings.subtitleColor) return blocks;

      return blocks.map(block => {
          // Skip read-only blocks
          if (block.isReadOnly) return block;

          let newText = block.text;
          
          if (settings.isBilingual) {
              // Split bilingual by newline. Usually 2 lines: Eng\nViet
              // We assume the last line is Vietnamese translation
              const lines = newText.split('\n');
              if (lines.length > 1) {
                  const lastLine = lines.pop(); // Remove Viet line
                  // Wrap Viet line in font tag
                  const coloredLastLine = `<font color="${settings.subtitleColor}">${lastLine}</font>`;
                  // Reassemble
                  newText = [...lines, coloredLastLine].join('\n');
              } else {
                  // Fallback if split fails
                   newText = `<font color="${settings.subtitleColor}">${newText}</font>`;
              }
          } else {
              // Standard mode: wrap entire text
              newText = `<font color="${settings.subtitleColor}">${newText}</font>`;
          }

          return { ...block, text: newText };
      });
  };

  // DEFINE HELPERS BEFORE USAGE
  const stopTranslation = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setStatus(AppStatus.Idle);
  }, []);

  const hasTranslations = React.useMemo(() => blocks.some(b => !b.isReadOnly && b.text !== b.originalText), [blocks]);

  const totalBlocksCount = blocks.length;

  const rangeCompletionPercent = React.useMemo(() => {
     if (rangeEnd < rangeStart || blocks.length === 0) return 0;
     const rangeBlocks = blocks.filter(b => b.id >= rangeStart && b.id <= rangeEnd && !b.isReadOnly);
     if (rangeBlocks.length === 0) return 0;
     const translatedCount = rangeBlocks.filter(b => b.text !== b.originalText).length;
     return (translatedCount / rangeBlocks.length) * 100;
  }, [blocks, rangeStart, rangeEnd]);

  const handleFinishAndReset = () => {
    setConfirmation({
      isOpen: true,
      title: "KẾT THÚC PHIÊN DỊCH",
      message: "Bạn có chắc chắn muốn kết thúc phiên làm việc này? Trang sẽ được tải lại mới hoàn toàn. Mọi dữ liệu chưa lưu (file tải xuống) sẽ bị mất vĩnh viễn.",
      confirmLabel: "Kết thúc & Tải lại",
      confirmColor: "red",
      onConfirm: () => {
        window.location.reload();
      }
    });
  };

  const handleResetTranslation = () => {
    setConfirmation({
      isOpen: true,
      title: "HOÀN TÁC DỊCH",
      message: "Bạn có chắc chắn muốn hoàn tác? Toàn bộ nội dung đã dịch trong phiên này sẽ bị xóa và quay về văn bản gốc ban đầu. Các chỉnh sửa tay của bạn cũng sẽ bị mất.",
      confirmLabel: "Đồng ý Hoàn tác",
      confirmColor: "blue",
      onConfirm: () => {
        stopTranslation();
        setBlocks(prev => prev.map(b => ({ 
            ...b, 
            text: b.originalText, 
            isManuallyEdited: false, 
            feedbackStatus: 'none' as const 
        })));
        setProgress(0);
        setCurrentBlockIndex(0);
        setStatus(AppStatus.Idle);
        setConfirmation(prev => ({ ...prev, isOpen: false }));
      }
    });
  };
  
  const handleSaveProject = () => {
     const projectData = {
         version: "1.0",
         filename: filename,
         blocks: blocks,
         context: context,
         settings: {
             model, tone, batchSize, rangeStart, rangeEnd, charRelationships,
             isCensorshipEnabled, isMetricConversionEnabled, 
             isCleanModeEnabled, isHiTranslationEnabled,
             isLyricsTranslationEnabled, isBilingual, subtitleColor, isColorEnabled,
             isSouthernStyle, customStyle
         }
     };
     const jsonString = JSON.stringify(projectData, null, 2);
     const safeName = filename.replace('.srt', '').replace('.json', '');
     downloadFile(`${safeName}_project.json`, jsonString);
     setShowDonatePopup(true);
  };

  const startTranslation = async () => {
    if (blocks.length === 0) return;
    
    setApiKey(userApiKey);
    setStatus(AppStatus.Translating);
    abortControllerRef.current = new AbortController();

    const blocksToTranslate = blocks.filter(b => b.id >= rangeStart && b.id <= rangeEnd && !b.isReadOnly);
    if (blocksToTranslate.length === 0) {
      alert("Không tìm thấy dòng sub nào trong phạm vi đã chọn (hoặc chỉ có dòng chữ ký).");
      setStatus(AppStatus.Idle);
      return;
    }

    let completedCount = 0;
    let localIndex = 0;
    let previousContext: string[] = [];

    while (localIndex < blocksToTranslate.length) {
      if (abortControllerRef.current?.signal.aborted) {
        setStatus(AppStatus.Idle);
        return;
      }
      const getDynamicContextSize = (count: number) => {
          if (count < 30) return 15;
          if (count < 70) return 30;
          if (count < 150) return 50;
          return 70;
      };
      const contextSize = getDynamicContextSize(batchSize);
      const currentBatch = blocksToTranslate.slice(localIndex, localIndex + batchSize);
      setCurrentBlockIndex(currentBatch[0].id);

      const currentSettings = settingsRef.current;
      const startPrev = Math.max(0, localIndex - contextSize);
      const prevBlocks = blocksToTranslate.slice(startPrev, localIndex);
      const prevContextStrings = prevBlocks.map(b => `[${b.id}] ${b.text}`);
      const startNext = localIndex + batchSize;
      const endNext = Math.min(blocksToTranslate.length, startNext + contextSize);
      const nextBlocks = blocksToTranslate.slice(startNext, endNext);
      const nextContextStrings = nextBlocks.map(b => `[${b.id}] ${b.originalText}`);

      try {
        const translations = await translateBatch(
            currentBatch, 
            prevContextStrings,
            nextContextStrings,
            model, 
            tone, 
            context, 
            currentSettings.charRelationships,
            currentSettings.isCensorshipEnabled,
            currentSettings.isMetricConversionEnabled,
            currentSettings.isCleanModeEnabled,
            currentSettings.isHiTranslationEnabled,
            currentSettings.isLyricsTranslationEnabled,
            currentSettings.isSouthernStyle,
            currentSettings.customStyle
        );
        
        setBlocks(prev => {
          const newBlocks = [...prev];
          translations.forEach((translatedText, id) => {
            const blockIndex = newBlocks.findIndex(b => b.id === id);
            if (blockIndex !== -1) {
              const originalBlock = newBlocks[blockIndex];
              let finalText = translatedText;
              
              // Apply Post-Processing Cleaning
              finalText = postProcessText(finalText, currentSettings.isCleanModeEnabled);

              if (currentSettings.isBilingual) {
                  let sourceText = originalBlock.originalText;
                  if (currentSettings.isCleanModeEnabled) {
                      sourceText = postProcessText(sourceText, true);
                  }
                  if (sourceText) {
                      finalText = `${sourceText}\n${finalText}`;
                  }
              }
              newBlocks[blockIndex] = { ...originalBlock, text: finalText, isManuallyEdited: false };
            }
          });
          return newBlocks;
        });

        const lastFewOfBatch = currentBatch.slice(-CONTEXT_WINDOW_SIZE);
        const newContextLines: string[] = [];
        lastFewOfBatch.forEach(b => {
             // Logic kept for potential future use or context tracking if needed
        });
        
        previousContext = newContextLines;
        localIndex += batchSize;
        completedCount += currentBatch.length;
        setProgress((completedCount / blocksToTranslate.length) * 100);

      } catch (err: any) {
        console.error("Batch failed after retries:", err);
        const errStr = String(err);
        if (errStr.includes('404')) {
            alert(`Model ${model} không phản hồi (404). Vui lòng đổi model khác.`);
            stopTranslation();
            return;
        }
        if (errStr.includes('429') || errStr.includes('RESOURCE_EXHAUSTED')) {
            // Enhanced Error Message for Rate Limit
            const cont = window.confirm("Đã vượt quá giới hạn API (Rate Limit).\n\nGợi ý:\n1. Giảm phạm vi dịch xuống < 400 dòng.\n2. Đổi sang API Key khác trong phần Cấu hình.\n\nBạn có muốn thử tiếp tục không?");
            if (!cont) {
                stopTranslation();
                return;
            }
        }
        localIndex += batchSize; 
      }
    }
    setStatus(AppStatus.Completed);
  };

  const handleDownload = () => {
    const blocksWithColor = injectColorTags(blocks);
    const content = buildSRT(blocksWithColor);
    const suffix = isBilingual ? '.bi.srt' : '.vi.srt';
    const newFilename = filename.replace('.srt', suffix);
    downloadFile(newFilename, content);
    setShowDonatePopup(true);
  };

  const handleDownloadRange = () => {
    const rangeBlocks = blocks.filter(b => b.id >= rangeStart && b.id <= rangeEnd);
    if (rangeBlocks.length === 0) {
        alert("Phạm vi trống.");
        return;
    }
    const blocksWithColor = injectColorTags(rangeBlocks);
    const content = buildSRT(blocksWithColor);
    const suffix = isBilingual ? '.bi.srt' : '.vi.srt';
    const newFilename = filename.replace('.srt', `.range_${rangeStart}-${rangeEnd}${suffix}`);
    downloadFile(newFilename, content);
    setShowDonatePopup(true);
  };

  return (
    <div className={`flex h-screen overflow-hidden font-sans select-none ${bgMain} ${textMain}`}>
      {/* Resizable Sidebar */}
      <div className={`flex-shrink-0 border-r flex flex-col h-full shadow-2xl z-20 transition-all duration-75 relative ${bgSidebar} ${borderMain}`} style={{ width: `${sidebarWidth}px` }}>
        {/* Header */}
        <div className={`p-6 border-b ${borderMain} ${bgSidebar}`}>
          <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <span className="text-white font-bold text-lg">AI</span>
                </div>
                <h1 className={`text-xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>Sub GG</h1>
              </div>
              <div className="relative">
                   <button onClick={() => setIsThemeDropdownOpen(!isThemeDropdownOpen)} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:text-white hover:border-zinc-700' : 'bg-white border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 shadow-sm'}`}>
                      <span>Giao diện</span>
                      <svg className={`w-3 h-3 transition-transform ${isThemeDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                   </button>
                   {isThemeDropdownOpen && (
                       <>
                          <div className="fixed inset-0 z-40" onClick={() => setIsThemeDropdownOpen(false)}></div>
                          <div className={`absolute right-0 mt-2 w-36 rounded-xl border shadow-xl z-50 overflow-hidden flex flex-col py-1 animate-fade-in ${isDarkMode ? 'bg-[#18181b] border-white/10' : 'bg-white border-gray-200'}`}>
                              <button onClick={() => { setThemeMode('light'); setIsThemeDropdownOpen(false); }} className={`flex items-center gap-3 px-4 py-2 text-xs font-medium transition-colors ${themeMode === 'light' ? 'text-blue-500' : (isDarkMode ? 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')}`}>Sáng</button>
                              <button onClick={() => { setThemeMode('dark'); setIsThemeDropdownOpen(false); }} className={`flex items-center gap-3 px-4 py-2 text-xs font-medium transition-colors ${themeMode === 'dark' ? 'text-blue-500' : (isDarkMode ? 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')}`}>Tối</button>
                              <button onClick={() => { setThemeMode('auto'); setIsThemeDropdownOpen(false); }} className={`flex items-center gap-3 px-4 py-2 text-xs font-medium transition-colors ${themeMode === 'auto' ? 'text-blue-500' : (isDarkMode ? 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')}`}>Tự động</button>
                          </div>
                       </>
                   )}
              </div>
          </div>
          <p className={`text-xs uppercase tracking-widest pl-11 font-semibold ${textSub}`}>Developed by @chimdien</p>
          <div className={`mt-4 text-[10px] italic leading-tight ${textSub} opacity-80 border-l-0 pl-0`}>
             Dịch phụ đề SRT bằng AI với model ngôn ngữ Gemini. Người dùng tự chịu trách nhiệm với nội dung tải lên và tải về. Website không lưu trữ bất kỳ thông tin nào.
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-4 space-y-5">
            <div className="group relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl opacity-20 group-hover:opacity-40 transition duration-300 blur"></div>
                <label className={`relative block w-full cursor-pointer rounded-xl border transition-all p-6 text-center ${isDarkMode ? 'bg-[#121212] hover:bg-zinc-900/80 border-white/10 hover:border-white/20' : 'bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300'}`} onDragOver={handleDragOver} onDrop={handleFileDrop}>
                    <div className="flex flex-col items-center justify-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center border group-hover:border-blue-500/50 transition-colors ${isDarkMode ? 'bg-black border-white/10' : 'bg-gray-50 border-gray-200'}`}>
                        <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                    </div>
                    <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>Tải lên file *.SRT hoặc *.JSON</p>
                        <p className={`text-xs mt-1 ${textSub}`}>{filename !== "subtitle.srt" ? filename : "Kéo thả hoặc click để chọn"}</p>
                    </div>
                    </div>
                    <input type="file" className="hidden" accept=".srt,.json" onChange={handleFileUpload} disabled={status === AppStatus.Translating} />
                </label>
            </div>

            <SettingsPanel 
                model={model} setModel={setModel}
                tone={tone} setTone={setTone}
                context={context} setContext={setContext}
                isLocked={status === AppStatus.Translating}
                apiStatus={apiStatus}
                onVerifyApi={handleVerifyApi}
                apiKey={userApiKey}
                setApiKey={(k) => { setUserApiKey(k); setApiStatus('unknown'); }}
                rangeStart={rangeStart}
                setRangeStart={setRangeStart}
                rangeEnd={rangeEnd}
                setRangeEnd={setRangeEnd}
                totalBlocks={blocks.length}
                batchSize={batchSize}
                setBatchSize={setBatchSize}
                charRelationships={charRelationships}
                setCharRelationships={setCharRelationships}
                isCensorshipEnabled={isCensorshipEnabled}
                setIsCensorshipEnabled={setIsCensorshipEnabled}
                isMetricConversionEnabled={isMetricConversionEnabled}
                setIsMetricConversionEnabled={setIsMetricConversionEnabled}
                isCleanModeEnabled={isCleanModeEnabled}
                setIsCleanModeEnabled={setIsCleanModeEnabled}
                isHiTranslationEnabled={isHiTranslationEnabled}
                setIsHiTranslationEnabled={setIsHiTranslationEnabled}
                isLyricsTranslationEnabled={isLyricsTranslationEnabled}
                setIsLyricsTranslationEnabled={setIsLyricsTranslationEnabled}
                onAutoAnalyze={handleAutoAnalyze}
                isAnalyzing={isAnalyzing}
                filename={filename}
                onFilenameAnalyze={handleFilenameAnalyze}
                isFilenameAnalyzing={isFilenameAnalyzing}
                onRelationshipAnalyze={handleRelationshipAnalyze}
                isRelationshipAnalyzing={isRelationshipAnalyzing}
                isDarkMode={isDarkMode}
                onSelectAllRange={handleSelectAllRange}
                isBilingual={isBilingual}
                setIsBilingual={setIsBilingual}
                subtitleColor={subtitleColor}
                setSubtitleColor={setSubtitleColor}
                isColorEnabled={isColorEnabled}
                setIsColorEnabled={setIsColorEnabled}
                onOpenRelEditor={() => setShowRelEditor(true)}
                isSouthernStyle={isSouthernStyle}
                setIsSouthernStyle={setIsSouthernStyle}
                customStyle={customStyle}
                setCustomStyle={setCustomStyle}
                onNormalizeStyle={handleNormalizeStyle}
                isNormalizingStyle={isNormalizingStyle}
                onOpenStyleEditor={() => setShowStyleEditor(true)}
            />
          </div>
        </div>
        
        {/* Footer Actions */}
        <div className={`border-t z-30 flex flex-col ${borderMain} ${bgSidebar} transition-all duration-300 ease-in-out`}>
            <div className={`w-full flex justify-center items-center p-2 cursor-pointer transition-colors ${isDarkMode ? 'hover:bg-white/5 text-zinc-500 hover:text-zinc-300' : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'}`} onClick={() => setIsFooterExpanded(!isFooterExpanded)}>
                <svg className={`w-4 h-4 transition-transform duration-300 ${isFooterExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path></svg>
            </div>
            <div className={`px-4 pb-4 flex flex-col gap-3 overflow-hidden transition-all duration-300 ${isFooterExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {status === AppStatus.Translating ? (
                    <button type="button" onClick={stopTranslation} className="w-full py-3.5 bg-red-600 hover:bg-red-500 text-white rounded-xl font-bold shadow-lg shadow-red-900/30 transition-all flex justify-center items-center gap-2 group">
                        <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span></span>
                        DỪNG DỊCH
                    </button>
                ) : (
                    <>
                        <button type="button" onClick={startTranslation} disabled={blocks.length === 0} title={hasTranslations ? "Vui lòng điền lại Phạm vi dịch nếu muốn dịch đoạn khác" : ""} className={`w-full py-3.5 rounded-xl font-bold shadow-lg transition-all flex justify-center items-center gap-2 text-sm uppercase tracking-wider ${hasTranslations ? 'bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white shadow-emerald-900/30' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-blue-900/30'} disabled:opacity-50 disabled:cursor-not-allowed`}>
                            {hasTranslations ? <>Dịch tiếp đoạn được chọn</> : <>Bắt đầu Dịch</>}
                        </button>
                        {hasTranslations && (
                             <button type="button" onClick={handleResetTranslation} title="Hoàn tác để redo lại lệnh trước" className={`w-full py-2.5 border font-medium text-xs transition-all flex justify-center items-center gap-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700 border-zinc-700 text-zinc-300 hover:text-white' : 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-700 hover:text-gray-900'}`}>Hoàn tác</button>
                        )}
                    </>
                )}
                {blocks.length > 0 && hasTranslations && (
                    <>
                    <div className="grid grid-cols-2 gap-2 pt-2">
                        <button type="button" onClick={handleDownload} className="w-full py-3 bg-emerald-600/10 hover:bg-emerald-600/20 border border-emerald-500/50 text-emerald-500 text-xs font-bold uppercase tracking-wider rounded-xl transition-all flex justify-center items-center gap-2">Tải toàn bộ</button>
                        <button type="button" onClick={handleDownloadRange} className="w-full py-3 bg-blue-600/10 hover:bg-blue-600/20 border border-blue-500/50 text-blue-500 text-xs font-bold uppercase tracking-wider rounded-xl transition-all flex justify-center items-center gap-2">Tải phần chọn</button>
                    </div>
                    <button type="button" onClick={handleSaveProject} className={`w-full py-2.5 border border-dashed font-medium text-xs transition-all flex justify-center items-center gap-2 rounded-xl mt-1 ${isDarkMode ? 'border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800' : 'border-gray-300 text-gray-600 hover:text-black hover:bg-gray-100'}`}>
                         <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path></svg>
                         Lưu dự án (JSON)
                    </button>
                    </>
                )}
                {hasTranslations && status !== AppStatus.Translating && (
                    <div className={`pt-2 border-t ${borderMain}`}>
                        <button type="button" onClick={handleFinishAndReset} title="Tải lại trang" className="w-full py-3 bg-red-600/10 hover:bg-red-600/20 border border-red-500/50 text-red-500 rounded-xl font-medium text-sm transition-all flex justify-center items-center gap-2">Kết thúc phiên dịch</button>
                    </div>
                )}
            </div>
        </div>
      </div>

      <div className={`w-1 cursor-col-resize z-50 transition-colors duration-150 flex items-center justify-center border-l ${isDarkMode ? 'bg-zinc-900 border-white/5 hover:bg-blue-600' : 'bg-gray-200 border-gray-300 hover:bg-blue-400'}`} onMouseDown={startResizing}><div className={`h-8 w-0.5 rounded ${isDarkMode ? 'bg-zinc-700' : 'bg-gray-400'}`}></div></div>

      <div className={`flex-1 flex flex-col h-full relative min-w-[400px] ${bgMain}`}>
         {/* ... Preview content omitted for brevity, structure remains similar ... */}
         {/* Just ensuring SubtitleRow and Minimap are rendered correctly */}
         <div className={`h-1.5 w-full z-10 ${isDarkMode ? 'bg-black' : 'bg-gray-200'}`}>
            <div className="h-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 transition-all duration-300 ease-out shadow-[0_0_15px_rgba(37,99,235,0.6)]" style={{ width: `${status === AppStatus.Translating ? progress : rangeCompletionPercent}%` }}></div>
        </div>
        <div className={`px-6 py-3 backdrop-blur border-b flex justify-between items-center gap-4 ${toolbarBg} ${borderMain}`}>
            {/* Stats Group */}
            <div className="flex-1 flex items-center gap-6 overflow-x-auto no-scrollbar">
               {/* ... Stats omitted ... */}
                <div className="flex flex-col"><span className={`text-[10px] uppercase tracking-wide whitespace-nowrap ${textSub}`}>Tổng số dòng</span><span className={`text-sm font-mono font-bold ${isDarkMode ? 'text-zinc-200' : 'text-gray-900'}`}>{totalBlocksCount}</span></div>
                {/* ... other stats ... */}
                <div className="flex flex-col"><span className={`text-[10px] uppercase tracking-wide whitespace-nowrap ${textSub}`}>Thời gian</span><span className={`text-sm font-mono font-bold ${status === AppStatus.Translating ? 'text-green-500 animate-pulse' : 'text-gray-400'}`}>{formatTime(elapsedTime)}</span></div>
            </div>
            <div className="flex items-center gap-4">
                 {status === AppStatus.Translating && (<div className="flex items-center gap-2 px-3 py-1 bg-blue-950/30 border border-blue-500/30 rounded-full whitespace-nowrap"><span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span></span><span className="text-xs text-blue-400 font-medium animate-pulse">Đang dịch...</span></div>)}
                
                {/* Separate Clean Up Button */}
                <button 
                    onClick={handleCleanUp} 
                    className="flex items-center gap-2 px-3 py-1.5 bg-pink-500/10 text-pink-500 hover:bg-pink-500/20 border border-pink-500/30 rounded-lg text-xs font-bold transition-all"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                    Dọn dòng trắng
                </button>

                <div className={`flex items-center gap-1 rounded-lg p-1 border ${isDarkMode ? 'bg-zinc-900 border-white/10' : 'bg-gray-100 border-gray-200'}`}>
                    <button onClick={handleZoomOut} className={`p-1 rounded transition-colors ${isDarkMode ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-gray-200 text-gray-600 hover:text-black'}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4"></path></svg></button>
                    <span className={`text-xs font-mono w-8 text-center select-none ${isDarkMode ? 'text-zinc-300' : 'text-gray-700'}`}>{zoomLevel}px</span>
                    <button onClick={handleZoomIn} className={`p-1 rounded transition-colors ${isDarkMode ? 'hover:bg-zinc-800 text-zinc-400 hover:text-white' : 'hover:bg-gray-200 text-gray-600 hover:text-black'}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg></button>
                </div>
            </div>
        </div>

        <div className="flex-1 flex overflow-hidden relative">
            <div className={`flex-1 overflow-y-auto p-6 space-y-3 relative custom-scrollbar ${bgMain}`} ref={scrollRef}>
              {blocks.length === 0 ? (
                <div className={`h-full flex flex-col items-center justify-center opacity-50 ${isDarkMode ? 'text-zinc-600' : 'text-gray-400'}`}>
                   <svg className="w-24 h-24 mb-6 stroke-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"></path></svg>
                   <p className="text-lg font-light">Chưa có nội dung phụ đề để hiển thị</p>
                   <p className="text-sm">Hãy tải lên file phụ đề *.srt để bắt đầu phép màu dịch thuật!</p>
                </div>
              ) : (
                 blocks.map((block) => (
                    <SubtitleRow
                        key={block.id}
                        block={block}
                        isProcessing={(status === AppStatus.Translating && block.id >= currentBlockIndex && block.id < currentBlockIndex + batchSize) || !!block.isRetranslating}
                        isTranslated={block.text !== block.originalText}
                        isInRange={block.id >= rangeStart && block.id <= rangeEnd}
                        isDarkMode={isDarkMode}
                        zoomLevel={zoomLevel}
                        onEdit={handleBlockEdit}
                        onFeedback={handleBlockFeedback}
                        onAiRetranslate={handleAiRetranslateAction}
                    />
                 ))
              )}
            </div>
            {blocks.length > 0 && (<Minimap blocks={blocks} scrollRef={scrollRef} isDarkMode={isDarkMode} />)}
        </div>
      </div>

      {/* CONFIRMATION MODAL */}
      {confirmation.isOpen && (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md p-4 animate-fade-in ${isDarkMode ? 'bg-black/80' : 'bg-white/50'}`}>
            <div className={`border rounded-2xl shadow-2xl max-w-md w-full p-6 scale-100 animate-scale-in transform transition-all ${isDarkMode ? 'bg-[#121212] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-3 mb-4">
                    {confirmation.confirmColor === 'red' ? (
                        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center"><svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg></div>
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center"><svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg></div>
                    )}
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{confirmation.title}</h3>
                </div>
                <p className={`text-sm leading-relaxed mb-6 pl-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>{confirmation.message}</p>
                <div className="flex gap-3">
                    <button onClick={() => setConfirmation(prev => ({ ...prev, isOpen: false }))} className={`flex-1 py-3 rounded-xl font-medium transition-colors ${isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Hủy bỏ</button>
                    <button onClick={confirmation.onConfirm} className={`flex-1 py-3 text-white rounded-xl font-bold shadow-lg transition-all ${confirmation.confirmColor === 'red' ? 'bg-red-600 hover:bg-red-500 shadow-red-500/30' : 'bg-blue-600 hover:bg-blue-500 shadow-blue-500/30'}`}>{confirmation.confirmLabel}</button>
                </div>
            </div>
        </div>
      )}
      
      {/* AI ANALYZE CONFIRM MODAL (Req 5) */}
      {showAnalyzeConfirm && (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md p-4 animate-fade-in ${isDarkMode ? 'bg-black/80' : 'bg-white/50'}`}>
             <div className={`border rounded-2xl shadow-2xl max-w-md w-full p-6 scale-100 animate-scale-in transform transition-all ${isDarkMode ? 'bg-[#121212] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center"><svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg></div>
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Xác nhận Phân tích</h3>
                </div>
                <p className={`text-sm leading-relaxed mb-6 pl-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>Tính năng này sẽ phân tích toàn bộ file phụ đề mà không sử dụng dữ liệu trên internet nên có thể không chính xác về nội dung. Thời gian xử lý có thể hơi lâu và tốn tài nguyên request API key. Cân nhắc trước khi ấn XÁC NHẬN.</p>
                <div className="flex gap-3">
                    <button onClick={() => setShowAnalyzeConfirm(false)} className={`flex-1 py-3 rounded-xl font-medium transition-colors ${isDarkMode ? 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>Bỏ qua</button>
                    <button onClick={executeAutoAnalyze} className={`flex-1 py-3 text-white rounded-xl font-bold shadow-lg transition-all bg-blue-600 hover:bg-blue-500 shadow-blue-500/30`}>Xác nhận</button>
                </div>
             </div>
        </div>
      )}
      
      {/* RELATIONSHIP EDITOR POPUP (Req 10) */}
      {showRelEditor && (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md p-4 animate-fade-in ${isDarkMode ? 'bg-black/90' : 'bg-white/70'}`}>
            <div className={`relative border rounded-2xl shadow-2xl max-w-2xl w-full h-[80vh] flex flex-col p-6 scale-100 animate-scale-in transform transition-all ${isDarkMode ? 'bg-[#121212] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Chỉnh sửa Quan hệ nhân vật</h3>
                    <button onClick={() => setShowRelEditor(false)} className={`p-2 rounded-full hover:bg-gray-200/20 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                </div>
                <div className="flex-1 relative">
                    <textarea 
                        className={`w-full h-full p-4 rounded-xl border resize-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${isDarkMode ? 'bg-black border-zinc-800 text-zinc-200' : 'bg-gray-50 border-gray-300 text-gray-800'}`}
                        value={charRelationships}
                        onChange={(e) => setCharRelationships(e.target.value)}
                        placeholder="Nhập chi tiết các mối quan hệ nhân vật..."
                    />
                </div>
                <div className="mt-4 flex justify-end">
                     <button onClick={() => setShowRelEditor(false)} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-lg transition-all">Lưu & Đóng</button>
                </div>
            </div>
        </div>
      )}

      {/* STYLE EDITOR POPUP (New) */}
      {showStyleEditor && (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md p-4 animate-fade-in ${isDarkMode ? 'bg-black/90' : 'bg-white/70'}`}>
            <div className={`relative border rounded-2xl shadow-2xl max-w-2xl w-full h-[80vh] flex flex-col p-6 scale-100 animate-scale-in transform transition-all ${isDarkMode ? 'bg-[#121212] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Chỉnh sửa Cá nhân hoá văn phong</h3>
                    <button onClick={() => setShowStyleEditor(false)} className={`p-2 rounded-full hover:bg-gray-200/20 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                </div>
                <div className="flex-1 relative">
                    <textarea 
                        className={`w-full h-full p-4 rounded-xl border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/50 ${isDarkMode ? 'bg-black border-zinc-800 text-zinc-200' : 'bg-gray-50 border-gray-300 text-gray-800'}`}
                        value={customStyle}
                        onChange={(e) => setCustomStyle(e.target.value)}
                        placeholder="Nhập chi tiết các yêu cầu văn phong và từ vựng..."
                    />
                </div>
                <div className="mt-4 flex justify-end">
                     <button onClick={() => setShowStyleEditor(false)} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-lg transition-all">Lưu & Đóng</button>
                </div>
            </div>
        </div>
      )}

      {/* DONATION POPUP */}
      {showDonatePopup && (
        <div className={`fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-md p-4 animate-fade-in ${isDarkMode ? 'bg-black/80' : 'bg-white/50'}`}>
             <div className={`relative rounded-2xl shadow-2xl max-sm w-full p-6 scale-100 animate-scale-in transform transition-all ${isDarkMode ? 'bg-[#121212] border border-white/10' : 'bg-white border-gray-200'}`}>
                <button onClick={() => setShowDonatePopup(false)} className={`absolute top-4 right-4 p-1 rounded-full transition-colors ${isDarkMode ? 'text-zinc-400 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-black hover:bg-gray-100'}`}><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg></button>
                <div className="text-center mb-6">
                    <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Ủng hộ nhà phát triển ❤️</h3>
                    <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>Ủng hộ nhà phát triển 1 ly cafe bằng cách quét QR chuyển khoản và donate tuỳ tâm.</p>
                </div>
                <div className="flex justify-center mb-2">
                  <div className="p-2 bg-white rounded-xl shadow-inner">
                      <img src="/donate.png" alt="QR Donate" className="w-64 h-auto object-contain rounded-lg" onError={(e) => { e.currentTarget.src = 'https://placehold.co/300x300?text=QR+Donate'; }} />
                  </div>
                </div>
                <button onClick={() => setShowDonatePopup(false)} className={`w-full mt-4 py-3 rounded-xl font-bold shadow-lg transition-all ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/30' : 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-200'}`}>Đóng</button>
             </div>
        </div>
      )}
    </div>
  );
};

export default App;
